// Overlay full-screen para el step `select-vistas` del Catálogo IA.
// Se renderiza encima de TODO (incluyendo el sheet del preset) cuando
// el step coincide. Layout:
//   • Header: Volver (back) + título + 2 pills "Con modelo" / "Solo producto".
//   • Grid: 3 columnas con las vistas de la categoría activa.
//   • Footer: strip de seleccionadas (cada thumb con X para deseleccionar)
//             + botón "Siguiente".
// Mobile: `fixed inset-0`. Desktop: `fixed inset-y-0 right-0` +
// `left: sidebarWidth` para que el sidebar siga visible.

import React, { useEffect, useRef, useState } from 'react';
import { Check, ChevronLeft, ChevronRight, Plus, Trash2, X } from 'lucide-react';
import { scrollToAndShake } from '../scrollShake';
import type { PoseV2 } from '../posesApi';
import type { MockProductV2 } from '../mockData';
import {
  GRUPOS_VISTA_PRODUCTO,
  VISTAS_SOLO_PRODUCTO,
  isVistaCompatibleConModelView,
  resolveVistaImageUrl,
  type VistaCatalogo,
} from '../vistasCatalogo';
import { VISTAS_MUEBLES, DESCRIPCION_VISTA_MUEBLE } from '../vistasMuebles';
// PRUEBA: miniaturas de ejemplo alternativas para el maniquí (ver `vistasPrueba.ts`).
import { miniaturaDeVistaPrueba } from '../vistasPrueba';
import { MARCO_PANTALLA, BORDE_PANTALLA } from '../../../components/PantallaChrome';
import { usePalabraDeModelo } from '../../../lib/nombreDeModelo';

export interface VistaPickerFullscreenProps {
  open: boolean;
  variant: 'mobile' | 'desktop';
  sidebarWidth: number;
  allPoses: PoseV2[];
  /** Poses custom (muñeco) de la empresa — tab "Mis poses". */
  customPoses: PoseV2[];
  /** Crear una pose custom desde acá (sube foto → muñeco). */
  onUploadCustomPose: (file: File) => void;
  /** true mientras se procesa la subida de una pose custom. */
  uploadingCustomPose: boolean;
  /** Borrar una pose custom propia. */
  onDeleteCustomPose: (pose: PoseV2) => void;
  selectedProducts: MockProductV2[];
  selectedVistas: VistaCatalogo[];
  onChange: (next: VistaCatalogo[]) => void;
  onBack: () => void;
  onNext: () => void;
  /**
   * Qué catálogo de vistas mostrar.
   *
   *   'catalogo' (default) — el del Catálogo IA: poses con modelo, ángulos
   *      solo-producto y "Mis poses", con las 3 pills arriba.
   *   'muebles' — el del Catálogo de muebles: 3 vistas fijas (fondo blanco /
   *      infografía / foto en contexto), SIN pills (no hay categorías que
   *      elegir) y sin poses ni modelo.
   *
   * Es una prop opcional para no tocar el comportamiento de ningún caller
   * existente. Ver ADR-582.
   */
  variantVistas?: 'catalogo' | 'muebles';
  /**
   * Render EMBEBIDO: en vez del overlay full-screen, el picker se dibuja como una
   * sección más dentro de una columna (la de la izquierda del paso 2 del Catálogo).
   *
   * Es el mismo componente y no un fork: la lógica que importa acá —qué vistas son
   * compatibles con los productos elegidos, las poses custom, el resolver de
   * thumbnails— es toda de este archivo, y duplicarla para cambiar el marco es lo que
   * ya dolió con las pantallas `…V2`. Lo que se apaga es el chrome del overlay: el
   * `fixed`, la barra de Volver/Siguiente (el CTA pasa a ser el del panel de al lado) y
   * el strip flotante de seleccionadas (no hay dónde flotar dentro de una columna).
   *
   * Sólo desktop: en mobile el paso sigue siendo la pantalla completa.
   */
  embebido?: boolean;
  /**
   * Dibuja un "Volver" ARRIBA del título, dentro del bloque del encabezado.
   *
   * Sólo aplica embebido: la rama full-screen ya tiene el suyo en la barra de acciones.
   * Va acá adentro y no en el contenedor de afuera para que quede dentro de la caja
   * blanca de la columna —suelto sobre el gris se leía como un control de la pantalla y
   * no de esta columna (ADR-2026-08-27-el-volver-vive-en-la-caja-del-titulo)—.
   */
  volverEmbebido?: () => void;
  /**
   * Sólo las vistas SIN modelo (los ángulos del producto): la pantalla arranca y se queda
   * en esa categoría, y las pills desaparecen porque no hay entre qué elegir.
   *
   * No se implementó filtrando la lista de afuera: las categorías las arma este archivo
   * (poses del catálogo, ángulos solo-producto, poses custom de la empresa) a partir de
   * fuentes distintas, así que el recorte tiene que decidirse acá.
   */
  soloVistasDeProducto?: boolean;
}

export const VistaPickerFullscreen: React.FC<VistaPickerFullscreenProps> = ({
  open,
  variant,
  sidebarWidth,
  allPoses,
  customPoses,
  onUploadCustomPose,
  uploadingCustomPose,
  onDeleteCustomPose,
  selectedProducts,
  selectedVistas,
  onChange,
  onBack,
  onNext,
  variantVistas = 'catalogo',
  embebido = false,
  volverEmbebido,
  soloVistasDeProducto = false,
}) => {
  // La palabra de pantalla para la persona de la foto — "Modelo" en Moda, "Avatar"
  // en el resto (ADR-2026-09-22-en-moda-el-modelo-se-llama-modelo).
  const palabra = usePalabraDeModelo();
  const esMuebles = variantVistas === 'muebles';
  // Arranca en "Solo producto", la primera pill de la fila
  // (ADR-2026-08-27-solo-producto-tambien-es-el-default): es el punto de partida de un
  // catálogo —el producto solo— y lo que sirve a cualquier rubro; las poses con modelo
  // son la variante. Abrir en "Con modelo" además contradecía el orden de las pills.
  const [activeCategory, setActiveCategory] = useState<
    'con-modelo' | 'solo-producto' | 'mis-pose'
  >('solo-producto');
  // Input file oculto para "Crear pose" (tab Mis poses).
  const fileRef = useRef<HTMLInputElement | null>(null);

  // Animación de entrada: subtle fade + slide-up al abrirse. Damos un
  // tick para que el primer render aplique los estilos "iniciales" y
  // el segundo (post-rAF) los finales — así la transición CSS se
  // dispara. Declarado ARRIBA del early return de `!open` para no
  // violar las Rules of Hooks.
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    if (!open) {
      setEntered(false);
      return;
    }
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, [open]);

  // Poses 1-persona transformadas a VistaCatalogo. Arrastramos
  // `areasPrincipales` (tipos de producto sugeridos de la pose) para que
  // el filtro de compatibilidad pueda decidir con qué productos aplica.
  const posesAsVistas: VistaCatalogo[] = React.useMemo(
    () =>
      allPoses
        .filter((p) => (p.cantidadPersonas ?? 1) === 1)
        .map((p) => ({
          id: p.id,
          nombre: p.nombre,
          imageUrl: p.imageUrl,
          kind: 'con-modelo' as const,
          areasPrincipales: p.areasPrincipales,
        })),
    [allPoses],
  );
  // Poses filtradas a las compatibles con AL MENOS un producto
  // seleccionado, mirando `areasPrincipales`. Así, si el user eligió un
  // zapato y una remera, no mostramos poses que enfocan la cabeza.
  const posesCompatibles = React.useMemo(
    () =>
      posesAsVistas.filter((v) =>
        selectedProducts.some((p) =>
          isVistaCompatibleConModelView(v, p.modelView),
        ),
      ),
    [posesAsVistas, selectedProducts],
  );
  // Vistas solo-producto filtradas a las compatibles con AL MENOS un
  // producto seleccionado.
  const vistasSoloProductoCompatibles = React.useMemo(
    () =>
      VISTAS_SOLO_PRODUCTO.filter((v) =>
        selectedProducts.some((p) =>
          isVistaCompatibleConModelView(v, p.modelView),
        ),
      ),
    [selectedProducts],
  );

  const isSelected = (vista: VistaCatalogo) =>
    selectedVistas.some((v) => v.kind === vista.kind && v.id === vista.id);
  const toggle = (vista: VistaCatalogo) => {
    if (isSelected(vista)) {
      onChange(
        selectedVistas.filter(
          (v) => !(v.kind === vista.kind && v.id === vista.id),
        ),
      );
    } else {
      onChange([...selectedVistas, vista]);
    }
  };
  const removeFromSelected = (vista: VistaCatalogo) =>
    onChange(
      selectedVistas.filter(
        (v) => !(v.kind === vista.kind && v.id === vista.id),
      ),
    );

  // Poses custom (muñeco) como vistas. Son universales: NO se filtran por
  // producto (el cliente las creó a propósito). Llevan `filename` para que
  // la generación arme `poses_custom:[{id,filename}]`.
  const customPosesAsVistas: VistaCatalogo[] = React.useMemo(
    () =>
      customPoses.map((p) => ({
        id: p.id,
        nombre: p.nombre,
        imageUrl: p.imageUrl,
        kind: 'mis-pose' as const,
        filename: p.filename,
      })),
    [customPoses],
  );

  // La categoría que manda. `activeCategory` sigue existiendo (las pills la escriben)
  // pero acá se ignora: si el modo es solo-producto, no hay otra pestaña a la que ir, y
  // el state sobrevive entre aperturas del picker —podría venir en "Con modelo" de una
  // vuelta anterior—.
  const categoriaActiva = soloVistasDeProducto ? 'solo-producto' : activeCategory;

  const currentVistas = esMuebles
    ? // Muebles: las 3 fijas, sin filtrar por `model_view`. Los productos de
      // este rubro no pasan por el categorizador de moda (su model_view es
      // null), así que filtrar los escondería a todos.
      VISTAS_MUEBLES
    : categoriaActiva === 'con-modelo'
      ? posesCompatibles
      : categoriaActiva === 'solo-producto'
        ? vistasSoloProductoCompatibles
        : customPosesAsVistas;

  // Resuelve el thumbnail de una vista probando los `model_view` de los
  // productos seleccionados — si alguno tiene un override matcheado en
  // `imageUrlByModelView`, lo usamos (p. ej. ropa muestra el plano de
  // prenda en vez de la zapatilla genérica). Sino cae al `imageUrl`
  // default.
  const resolveImageForSelected = (vista: VistaCatalogo): string | null => {
    // PRUEBA: si la vista tiene una miniatura de ensayo, gana. Va acá arriba —y no en la
    // tarjeta— porque este resolver es el único punto por el que pasan la grilla y los
    // strips de seleccionadas: puesto más abajo, las dos vistas de la misma cosa
    // mostrarían fotos distintas.
    const dePrueba = miniaturaDeVistaPrueba(vista.id);
    if (dePrueba) return dePrueba;
    if (!vista.imageUrlByModelView) return vista.imageUrl;
    for (const product of selectedProducts) {
      const url = resolveVistaImageUrl(vista, product.modelView);
      if (url && url !== vista.imageUrl) return url;
    }
    return vista.imageUrl;
  };

  const renderVistaCard = (vista: VistaCatalogo) => {
    const selected = isSelected(vista);
    const thumbUrl = resolveImageForSelected(vista);
    // El packshot NO se distingue por cómo se encuadra —todas las miniaturas van en
    // `cover`— sino por el color de su nombre: son fotos casi blancas, así que el
    // degradado negro que despega el texto sobre una pose se lee ahí como un fondo gris
    // (ADR-2026-08-27-las-miniaturas-de-vista-entran-enteras).
    const ejemploDeProducto = vista.kind === 'solo-producto';
    return (
      <button
        key={`${vista.kind}-${vista.id}`}
        type="button"
        onClick={() => toggle(vista)}
        className={`relative aspect-[3/4] rounded-xl overflow-hidden ${
          ejemploDeProducto ? 'bg-white' : 'bg-neutral-100'
        } active:scale-[0.98] transition-all ${
          selected ? 'ring-2 ring-brand-primary' : 'ring-1 ring-black/[0.06]'
        }`}
      >
        {thumbUrl ? (
          <img
            src={thumbUrl}
            alt={vista.nombre}
            loading="lazy"
            // `cover` sin padding, también para los packshots: su fondo no es blanco
            // puro (#F6–#FA contra el #FFF de la tarjeta), así que dibujados con
            // `contain` recortaban un rectángulo gris visible alrededor de la foto. Y
            // no hay un gris único con el que pintar la tarjeta —cada packshot trae el
            // suyo—, así que la salida es que la foto llene la caja y su propio fondo
            // sea el fondo. El recorte es del 7% del alto (fotos 0.80 en una caja 0.75)
            // y son packshots centrados con aire: no se come nada del producto.
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : esMuebles ? (
          // Las vistas de muebles todavía no tienen thumbnail de ejemplo, así
          // que en vez del placeholder "sin preview" la card se apoya en el
          // texto: nombre + qué genera. Cuando lleguen los ejemplos de diseño,
          // esta rama desaparece sola (pasa a haber `thumbUrl`).
          <div className="absolute inset-0 flex flex-col justify-center px-3 text-left">
            <p className="text-[14px] font-semibold text-brand-black/90 leading-tight">
              {vista.nombre}
            </p>
            <p className="text-[12px] text-[#3C3C43]/60 leading-snug mt-1">
              {DESCRIPCION_VISTA_MUEBLE[vista.id] ?? ''}
            </p>
          </div>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-[#3C3C43]/40 text-[12.5px]">
            sin preview
          </div>
        )}
        {/* Cómo se despega el nombre de la foto. Sin thumbnail (muebles) no va nada:
            taparía el texto oscuro de arriba.
            Ver ADR-2026-08-27-las-miniaturas-de-vista-entran-enteras. */}
        {!(esMuebles && !thumbUrl) && (
          // El MISMO degradado en las prendas y en las poses. Las de producto llevaban un
          // chip blanco del ancho del texto —para no desvanecer el ruedo de la prenda—,
          // pero en una grilla que mezcla los dos tipos dos tratamientos distintos se
          // leen como dos clases de tarjeta, cuando son la misma decisión. Se unifica al
          // degradado (ADR-2026-08-27-un-solo-tratamiento-para-el-nombre-de-la-vista),
          // que supersede a ADR-2026-08-27-las-miniaturas-de-vista-entran-enteras en este
          // punto.
          <>
            <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/55 via-black/10 to-transparent pointer-events-none" />
            <p className="absolute bottom-2 left-2 right-2 text-left text-white text-[12.5px] font-medium leading-tight drop-shadow">
              {vista.nombre}
            </p>
          </>
        )}
        {selected && (
          <span className="absolute top-2 right-2 w-6 h-6 inline-flex items-center justify-center rounded-full bg-brand-primary text-white">
            <Check className="w-3.5 h-3.5" strokeWidth={3} />
          </span>
        )}
      </button>
    );
  };

  // Card "Crear pose" del tab Mis poses — dispara el file picker; spinner
  // mientras el muñeco se genera.
  const renderCreatePoseCard = () => (
    <button
      type="button"
      onClick={() => fileRef.current?.click()}
      disabled={uploadingCustomPose}
      className="relative aspect-[3/4] rounded-xl border-2 border-dashed border-brand-primary/40 bg-brand-primary/[0.04] hover:bg-brand-primary/[0.08] disabled:opacity-60 disabled:cursor-not-allowed flex flex-col items-center justify-center gap-2 text-brand-primary transition-colors active:scale-[0.98]"
    >
      {uploadingCustomPose ? (
        <>
          <span className="w-6 h-6 rounded-full border-2 border-brand-primary/30 border-t-brand-primary animate-spin" />
          <span className="text-[11.5px] font-medium px-2 text-center leading-tight">
            Creando…
          </span>
        </>
      ) : (
        <>
          <Plus className="w-6 h-6" strokeWidth={2.5} />
          <span className="text-[12.5px] font-semibold px-2 text-center leading-tight">
            Crear pose
          </span>
        </>
      )}
    </button>
  );

  // Card de una pose custom — toggle de selección + botón borrar. Es un
  // <div> (no <button>) para poder anidar el botón de borrar adentro.
  const renderMisPoseCard = (pose: PoseV2) => {
    const vista: VistaCatalogo = {
      id: pose.id,
      nombre: pose.nombre,
      imageUrl: pose.imageUrl,
      kind: 'mis-pose',
      filename: pose.filename,
    };
    const selected = isSelected(vista);
    return (
      <div
        key={`mis-pose-${pose.id}`}
        role="button"
        tabIndex={0}
        onClick={() => toggle(vista)}
        className={`relative aspect-[3/4] rounded-xl overflow-hidden bg-neutral-100 cursor-pointer active:scale-[0.98] transition-all ${
          selected ? 'ring-2 ring-brand-primary' : 'ring-1 ring-black/[0.06]'
        }`}
      >
        {pose.imageUrl ? (
          <img
            src={pose.imageUrl}
            alt={pose.nombre}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-[#3C3C43]/40 text-[12.5px]">
            sin preview
          </div>
        )}
        <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/55 via-black/10 to-transparent pointer-events-none" />
        <p className="absolute bottom-2 left-2 right-2 text-left text-white text-[12.5px] font-medium leading-tight drop-shadow">
          {pose.nombre}
        </p>
        {/* Borrar — esquina superior izquierda. */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDeleteCustomPose(pose);
          }}
          aria-label={`Borrar ${pose.nombre}`}
          className="absolute top-2 left-2 w-6 h-6 inline-flex items-center justify-center rounded-full bg-black/55 backdrop-blur-sm text-white hover:bg-black/70 active:scale-95 transition-all"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
        {selected && (
          <span className="absolute top-2 right-2 w-6 h-6 inline-flex items-center justify-center rounded-full bg-brand-primary text-white">
            <Check className="w-3.5 h-3.5" strokeWidth={3} />
          </span>
        )}
      </div>
    );
  };

  /**
   * La grilla partida en secciones con subtítulo.
   *
   * Sólo en solo-producto, que es la categoría que mezcla dos cosas distintas: fotos del
   * producto solo ("Plano") y fotos vestidas sin persona ("Maniquí invisible"). Con las
   * dos en una grilla corrida hay que leer los nombres de a uno para saber qué es cada
   * cual.
   *
   * Si de un grupo no quedó ninguna vista compatible con los productos elegidos, ese
   * grupo no existe; y si sobrevive uno solo, va sin subtítulo — un título único arriba
   * de todo no separa nada.
   */
  // `descripcion` salió del shape: los grupos ya no la dibujan
  // (ADR-2026-08-27-cada-categoria-con-su-titulo). Sigue en `GRUPOS_VISTA_PRODUCTO` como
  // dato, sin consumir.
  const secciones: {
    label: string | null;
    vistas: VistaCatalogo[];
  }[] = React.useMemo(() => {
    const todas = [{ label: null, vistas: currentVistas }];
    // "Con modelo" es UNA sola familia, pero igual lleva encabezado: sin él, esa grilla
    // arrancaba pelada mientras la de al lado venía titulada, y el salto entre pills se
    // leía como dos pantallas distintas (ADR-2026-08-27-cada-categoria-con-su-titulo).
    if (categoriaActiva === 'con-modelo' && !esMuebles)
      return [{ label: `Poses con ${palabra.singular}`, vistas: currentVistas }];
    if (categoriaActiva !== 'solo-producto' || esMuebles) return todas;
    const porGrupo = GRUPOS_VISTA_PRODUCTO.map((g) => ({
      label: g.label as string | null,
      vistas: currentVistas.filter((v) => v.grupo === g.id),
    })).filter((g) => g.vistas.length > 0);
    // Sin `grupo` (una vista nueva a la que se le olvidó ponerlo) no se pierde: cae en
    // una sección final sin título.
    const sueltas = currentVistas.filter((v) => !v.grupo);
    if (sueltas.length > 0)
      porGrupo.push({ label: null, vistas: sueltas });
    if (porGrupo.length <= 1) return todas;
    return porGrupo;
  }, [categoriaActiva, esMuebles, currentVistas]);

  const isMobile = variant === 'mobile';

  if (!open) return null;

  // Padding horizontal del contenido: adentro de una columna, el `sm:px-8` del overlay
  // deja las tarjetas pegadas al borde de la caja blanca.
  const px = embebido ? 'px-5' : 'px-5 sm:px-8';
  // Menos columnas embebido: la columna mide poco más de la mitad de la pantalla, y las
  // 5 del overlay dejaban las vistas del tamaño de una estampilla.
  const Titulo = embebido ? 'h2' : 'h1';
  const columnasGrid = embebido ? '' : 'grid-cols-2 sm:grid-cols-4 lg:grid-cols-5';

  /**
   * Cuántas columnas tiene la grilla embebida: las que entren, no un número fijo.
   *
   * Era `grid-cols-3 lg:grid-cols-6` (ADR-2026-08-27-seis-vistas-por-fila), pensado para
   * que "Con modelo" —20 poses— cerrara en cuatro filas. El problema es que seis es un
   * número, no un tamaño: en la columna de ~615px que quedaba en un monitor de 1440 cada
   * tarjeta medía 93px, y a ese ancho los nombres largos ("Plano posterior", "Maniquí
   * invisible frontal") se desbordaban sobre la foto. Y al revés: en un monitor grande
   * seguían siendo seis, sin aprovechar el ancho.
   *
   * Con `auto-fill` + `minmax(168px, 1fr)` la tarjeta tiene un PISO y la cuenta sale del
   * ancho real: cuatro columnas en un monitor de 1440 y seis en uno de 1920, con la
   * tarjeta quedando en ~185-190px en los dos. 168 es el piso que hace entrar "Maniquí
   * invisible frontal" —el nombre más largo del catálogo— en un solo renglón; con 132 la
   * grilla llegaba a ocho columnas de 140px y el nombre seguía partiéndose en dos.
   * Es un piso y no un número de columnas a propósito: crecer la ventana suma columnas
   * sin achicar la tarjeta, así que "Con modelo" —20 poses— cierra en cinco filas en la
   * columna angosta y en cuatro cuando hay lugar.
   *
   * En `style` y no en una clase: Tailwind no genera `repeat(auto-fill, …)` arbitrario sin
   * escribir el valor entero entre corchetes, y ahí el número deja de leerse.
   * Ver ADR-2026-09-04-las-vistas-se-reparten-por-ancho-y-no-por-numero.
   */
  const gridEmbebidoStyle: React.CSSProperties | undefined = embebido
    ? { gridTemplateColumns: 'repeat(auto-fill, minmax(168px, 1fr))' }
    : undefined;

  // Cuerpo del picker: título, pills y grilla. Es lo ÚNICO que comparten el overlay
  // full-screen y el render embebido — el resto (marco, barra de acciones, strip
  // flotante) es chrome de uno o del otro.
  //
  // En mobile el título + pills viven adentro del scroll (las pills `sticky top-0` para
  // que queden pegadas arriba al scrollear). En desktop pasa lo mismo: el título
  // scrollea, los pills se quedan.
  const cuerpoScrolleable = (
      <div className="flex-1 min-h-0 overflow-y-auto">
        {/* Title block — scrollea. En mobile dejamos padding-top extra
            para que el chrome flotante del Sidebar no tape el título. */}
        <div
          className={`${px} pb-3 ${embebido ? 'pt-5' : 'sm:pt-6'}`}
          style={{
            paddingTop: isMobile
              ? 'calc(env(safe-area-inset-top, 0px) + 3.5rem + 1rem)'
              : undefined,
          }}
        >
          {embebido && volverEmbebido && (
            <button
              type="button"
              onClick={volverEmbebido}
              className="mb-2 inline-flex items-center gap-1 text-brand-primary text-[14px] hover:opacity-75 transition-opacity"
            >
              <ChevronLeft className="w-4 h-4" />
              Volver
            </button>
          )}
          {/* Embebido baja a `h2` de 20px, el mismo tamaño que los títulos de sección de
              "Imagen con modelo": acá esto no es el título de la pantalla sino el de una
              columna al lado de otra. El tag además NO es cosmético — el shell tiene un
              `.shell-seccion h1 { font-size: 32px }` que le gana por specificity a
              cualquier `text-[…]` y dejaba el título en 32px. */}
          <Titulo
            className={`font-semibold text-brand-black/90 leading-tight ${
              embebido ? 'text-[20px]' : 'text-[22px] sm:text-[26px]'
            }`}
          >
            Elegí las vistas
          </Titulo>
          <p className="text-[13.5px] sm:text-[14.5px] text-[#3C3C43]/60 mt-1">
            {esMuebles
              ? // Sin "compatible": en muebles todas las vistas aplican a todos
                // los productos, así que la aclaración solo confundiría.
                'Vamos a generar una imagen por cada vista × producto.'
              : 'Vamos a generar una imagen por cada vista × producto compatible.'}
          </p>
        </div>

        {/* Sticky pills — quedan pegadas al tope cuando el user
            scrollea. En mobile, ofseteamos `top` por el safe-area-inset
            + altura del header del Sidebar para que no se solapen con
            el chrome flotante (back/menu/credits pills).

            En muebles NO van: hay una sola categoría (3 vistas fijas), así que
            una barra de filtros con un solo filtro sería ruido. */}
        <div
          // Fondo: el de la superficie donde vive. Embebido es una tarjeta blanca, así
          // que blanco; en el overlay mobile la pantalla es gris y la franja blanca de
          // los pills partía la pantalla en dos. Opaco igual —los pills son sticky y la
          // grilla les pasa por debajo.
          // `whitespace-nowrap` + `shrink-0` en los pills y scroll horizontal acá: sin
          // eso, en un teléfono los tres se apretaban y la etiqueta se partía en dos
          // renglones con el número colgando al costado ("Solo / producto  5"). Ahora
          // cada pastilla es una línea y, si los tres no entran, la fila se desliza
          // (ADR-2026-09-02-los-pills-de-vistas-no-parten-la-etiqueta).
          className={`sticky z-10 ${px} py-3 overflow-x-auto scrollbar-hide ${
            isMobile && !embebido ? 'bg-surface-mobile' : 'bg-white'
          } items-center gap-2 ${
            esMuebles || soloVistasDeProducto ? 'hidden' : 'flex'
          }`}
          style={{
            top: isMobile
              ? 'calc(env(safe-area-inset-top, 0px) + 3.5rem)'
              : 0,
          }}
        >
          {/* "Solo producto" ABRE la fila: es la forma más básica de fotografiar
              un catálogo —el producto solo— y la que sirve a cualquier rubro. Las
              poses con modelo son la variante, no el punto de partida
              (ADR-2026-08-27-solo-producto-abre-la-fila). */}
          <button
            type="button"
            onClick={() => setActiveCategory('solo-producto')}
            className={`shrink-0 whitespace-nowrap inline-flex items-center px-4 py-2 rounded-full text-[14px] font-medium transition-colors border ${
              activeCategory === 'solo-producto'
                ? 'bg-brand-black text-white border-brand-black'
                : 'bg-white text-brand-black/85 border-brand-black/30 hover:bg-brand-black/[0.04]'
            }`}
          >
            Solo producto
            {vistasSoloProductoCompatibles.length > 0 && (
              <span
                className={`ml-1.5 text-[12.5px] ${
                  activeCategory === 'solo-producto'
                    ? 'text-white/80'
                    : 'text-[#3C3C43]/55'
                }`}
              >
                {vistasSoloProductoCompatibles.length}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={() => setActiveCategory('con-modelo')}
            className={`shrink-0 whitespace-nowrap inline-flex items-center px-4 py-2 rounded-full text-[14px] font-medium transition-colors border ${
              activeCategory === 'con-modelo'
                ? 'bg-brand-black text-white border-brand-black'
                : 'bg-white text-brand-black/85 border-brand-black/30 hover:bg-brand-black/[0.04]'
            }`}
          >
            Con {palabra.singular}
            {posesAsVistas.length > 0 && (
              <span
                className={`ml-1.5 text-[12.5px] ${
                  activeCategory === 'con-modelo'
                    ? 'text-white/80'
                    : 'text-[#3C3C43]/55'
                }`}
              >
                {posesAsVistas.length}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={() => setActiveCategory('mis-pose')}
            className={`shrink-0 whitespace-nowrap inline-flex items-center px-4 py-2 rounded-full text-[14px] font-medium transition-colors border ${
              activeCategory === 'mis-pose'
                ? 'bg-brand-black text-white border-brand-black'
                : 'bg-white text-brand-black/85 border-brand-black/30 hover:bg-brand-black/[0.04]'
            }`}
          >
            Mis poses
            {customPosesAsVistas.length > 0 && (
              <span
                className={`ml-1.5 text-[12.5px] ${
                  activeCategory === 'mis-pose'
                    ? 'text-white/80'
                    : 'text-[#3C3C43]/55'
                }`}
              >
                {customPosesAsVistas.length}
              </span>
            )}
          </button>
        </div>

        {/* Grid. Padding-bottom extra en desktop para que las cards no
            queden ocultas detrás del floating strip de seleccionadas. El id
            es el target del scroll+shake al intentar seguir sin vistas
            (ADR-555). */}
        <div
          id="vistas-grid-zone"
          // El `pb-32` del overlay es para que el strip flotante de seleccionadas no
          // tape la última fila; embebido no hay strip, así que sobra.
          className={`${px} py-5 ${embebido ? 'pb-5' : 'sm:pb-32'}`}
        >
          {/* El `!esMuebles` no es redundante: `activeCategory` sobrevive entre
              aperturas del picker (el componente queda montado con `open`
              false). Sin el guard, un user que dejó "Mis poses" activo en el
              Catálogo IA y después entra al de muebles vería la UI de crear
              poses, que ahí no aplica. */}
          {!esMuebles && categoriaActiva === 'mis-pose' ? (
            <>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) onUploadCustomPose(f);
                  e.currentTarget.value = '';
                }}
              />
              <p className="text-[13px] text-[#3C3C43]/55 mb-3 -mt-1">
                Subí una foto de una pose y Atlas crea un muñeco que la copia.
                Después la usás para armar tu catálogo.
              </p>
              <div
                className={`grid gap-2 sm:gap-3 ${columnasGrid}`}
                style={gridEmbebidoStyle}
              >
                {renderCreatePoseCard()}
                {customPoses.map((p) => renderMisPoseCard(p))}
              </div>
            </>
          ) : currentVistas.length === 0 ? (
            <p className="text-[14px] text-[#3C3C43]/55 mt-4">
              No hay vistas disponibles en esta categoría para los productos
              elegidos.
            </p>
          ) : (
            secciones.map(({ label, vistas }) => (
              <div key={label ?? 'todas'} className="mt-6 first:mt-0">
                {label && (
                  // Título de grupo con el mismo peso que un subtítulo de sección (no la
                  // etiqueta chica en mayúsculas de antes): estas dos maneras de fotografiar
                  // el producto son la decisión de la pantalla, no una etiqueta de orden.
                  // Sin la bajada: el nombre del grupo ("Plano", "Maniquí invisible")
                  // ya dice qué es cada familia, y la explicación empujaba la grilla
                  // hacia abajo en cada cambio de pill. 17.5px —dos más que antes— para
                  // que el encabezado se lea antes que las tarjetas que ordena
                  // (ADR-2026-08-27-cada-categoria-con-su-titulo).
                  <div className="mb-3">
                    <h3 className="text-[17.5px] font-semibold text-brand-black/90 leading-tight">
                      {label}
                    </h3>
                  </div>
                )}
                <div
                  className={`grid gap-2 sm:gap-3 ${columnasGrid}`}
                  style={gridEmbebidoStyle}
                >
                  {vistas.map(renderVistaCard)}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
  );

  if (embebido) {
    return (
      // Misma caja que las secciones de "Imagen con modelo" (`sectionWrap` de
      // `DesignModeloPanel`): tarjeta blanca con borde, alto de la columna y el cuerpo
      // scrolleando adentro. Es lo que hace que el paso se lea igual en los dos modos.
      <section className="h-full min-h-0 rounded-2xl bg-white border border-neutral-200 flex flex-col overflow-hidden">
        {cuerpoScrolleable}
      </section>
    );
  }

  return (
    <div
      // El marco flotante sale de `PantallaChrome`, compartido con las pantallas de
      // modo y el asistente (ADR-2026-08-03-chrome-de-pantalla-compartido).
      // `right`/`top` salieron del `style` inline y pasaron a clases: un estilo
      // inline no se puede meter en una media query, así que ahí ningún `lg:` los
      // alcanzaba. `left` se queda inline porque depende de `isMobile`, y
      // `sidebarWidth` ya trae el aire de ese lado (margen + ancho + margen).
      // `flex` y no sólo `flex-col`: sin el display, `flex-col` no hace NADA y el
      // `flex-1 min-h-0 overflow-y-auto` del cuerpo tampoco —el contenedor era un
      // bloque de alto automático, así que la grilla crecía hasta su alto natural, no
      // scrolleaba por dentro y empujaba la barra del CTA fuera de la pantalla. En
      // mobile eso dejaba el paso sin salida: no se podía scrollear ni confirmar
      // (ADR-2026-09-02-el-picker-de-vistas-en-mobile-no-scrolleaba).
      className={`fixed inset-0 z-[110] bg-surface-mobile md:bg-surface-content md:rounded-tl-[26px] md:rounded-bl-[26px] md:overflow-hidden ${MARCO_PANTALLA} ${BORDE_PANTALLA} flex flex-col transition-transform duration-200 ease-out`}
      style={{
        // El picker cubre toda la pantalla (el `inset-0` de la clase) para que
        // NO se vea el sheet del choose-product detrás. El chrome flotante del
        // Sidebar mobile (back/menu/credits pills) queda visible
        // encima del bg blanco del propio picker — mismo patrón que
        // las otras pantallas con `mobileHeaderTransparent: true`.
        left: isMobile ? 0 : sidebarWidth,
        // Sin fade de opacidad: el overlay tiene fondo OPACO. Animar la opacidad
        // del elemento entero lo dejaba semi-transparente durante el fundido y se
        // veía la home de atrás (imágenes de modelo colándose). Solo deslizamos.
        transform: entered ? 'translateY(0)' : 'translateY(8px)',
      }}
    >
      {/* Cobertura blanca opaca en la zona del chrome flotante mobile.
          Los pills del Sidebar (back/menu/credits) son `bg-white/95`
          translúcidos, así que sin esto las cards que scrollean por
          debajo se ven filtrándose entre los gaps de los pills. Esta
          capa absoluta dentro del wrapper queda detrás del chrome del
          Sidebar (z-50) pero encima del scroll del picker. */}
      {isMobile && (
        <div
          aria-hidden="true"
          // Del MISMO gris que la pantalla (`bg-surface-mobile`) y no blanco: es una
          // tapa para que las cards no se cuelen entre los pills del chrome, no una
          // superficie propia, y en blanco se leía como una barra de título que no
          // existe (ADR-2026-09-02-el-picker-de-vistas-en-mobile-no-scrolleaba).
          className="absolute inset-x-0 top-0 bg-surface-mobile pointer-events-none z-[5]"
          style={{
            height: 'calc(env(safe-area-inset-top, 0px) + 3.5rem)',
          }}
        />
      )}

      {/* Desktop only: sticky top bar con Volver (left) + Siguiente (right).
          El botón Siguiente flota arriba a la derecha siempre visible —
          el user puede confirmar sin scrollear al fondo. */}
      {!isMobile && (
        <div className="shrink-0 z-[20] px-8 pt-5 pb-4 flex items-center justify-between bg-white">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-1 text-brand-primary text-[14px] hover:opacity-75 transition-opacity"
          >
            <ChevronLeft className="w-4 h-4" />
            Volver
          </button>
          {/* Sin vistas: botón atenuado pero clickeable — hint en la card
              que asoma abajo + scroll+shake a la grilla (ADR-555). */}
          <div className="relative inline-flex flex-col items-stretch">
            <div
              aria-hidden
              className={`absolute inset-0 rounded-[22px] bg-white/85 backdrop-blur-md shadow-md ring-1 ring-black/[0.04] transition-opacity duration-300 ${
                selectedVistas.length === 0 ? 'opacity-100' : 'opacity-0'
              }`}
            />
            <div className="relative z-10">
              <button
                type="button"
                onClick={() => {
                  if (selectedVistas.length === 0) {
                    scrollToAndShake('vistas-grid-zone');
                    return;
                  }
                  onNext();
                }}
                aria-disabled={selectedVistas.length === 0}
                className={`w-full inline-flex items-center justify-center gap-2 py-2.5 px-5 rounded-full bg-brand-primary hover:bg-brand-primary-hover text-white text-[14.5px] font-semibold shadow-md transition-colors ${
                  selectedVistas.length === 0 ? 'opacity-60' : ''
                }`}
              >
                Siguiente
                {selectedVistas.length > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-white/25 text-[12.5px] font-semibold">
                    {selectedVistas.length}
                  </span>
                )}
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            <div
              className={`relative z-10 w-0 min-w-full overflow-hidden transition-all duration-300 ease-out ${
                selectedVistas.length === 0
                  ? 'max-h-16 opacity-100'
                  : 'max-h-0 opacity-0'
              }`}
            >
              <p className="pt-2 pb-1.5 px-3 text-center text-[12.5px] leading-snug text-[#3C3C43]/70">
                Seleccioná al menos una vista para continuar.
              </p>
            </div>
          </div>
        </div>
      )}
      {cuerpoScrolleable}

      {/* Mobile footer: thumbnail strip + botón Siguiente full-width.
          En desktop esto se reemplaza por el botón en la sticky top bar
          + un floating strip estilo MultiSelectedStrip (abajo). */}
      {isMobile && (
        <div
          className="shrink-0 border-t border-black/[0.06] bg-white px-5 pt-3 pb-4"
          style={{
            paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
          }}
        >
          {selectedVistas.length > 0 && (
            <div className="flex items-center gap-2 mb-3 overflow-x-auto scrollbar-hide -mx-2 px-2 pt-1.5">
              {selectedVistas.map((v) => {
                // `resolveImageForSelected` y NO `v.imageUrl`: el default de
                // vistas como "Plano frente" es la foto de una ZAPATILLA, y la
                // versión de prenda vive en `imageUrlByModelView`. La grilla ya
                // resolvía por el `model_view` del producto; el strip mostraba
                // el crudo, así que con una remera elegida las miniaturas
                // salían con zapatillas.
                const thumbUrl = resolveImageForSelected(v);
                return (
                <div key={`${v.kind}-${v.id}`} className="shrink-0 relative">
                  <div className="w-14 h-14 rounded-lg overflow-hidden bg-neutral-100 ring-1 ring-black/[0.06]">
                    {thumbUrl ? (
                      <img
                        src={thumbUrl}
                        alt={v.nombre}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[#3C3C43]/40 text-[11px] text-center px-1">
                        {v.nombre}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFromSelected(v);
                    }}
                    aria-label={`Quitar ${v.nombre}`}
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 inline-flex items-center justify-center rounded-full bg-brand-black text-white shadow-md hover:bg-brand-black/80 active:scale-95 transition-all"
                  >
                    <X className="w-3 h-3" strokeWidth={3} />
                  </button>
                </div>
                );
              })}
            </div>
          )}
          {/* Sin vistas: hint que emerge por ARRIBA del botón + scroll+shake
              a la grilla (patrón influencer, ADR-555). */}
          <div className="relative">
            <div
              aria-hidden
              className={`absolute inset-0 rounded-2xl bg-white/85 backdrop-blur-md shadow-md ring-1 ring-black/[0.04] transition-opacity duration-300 ${
                selectedVistas.length === 0 ? 'opacity-100' : 'opacity-0'
              }`}
            />
            <div
              className={`relative z-10 overflow-hidden transition-all duration-300 ease-out ${
                selectedVistas.length === 0
                  ? 'max-h-16 opacity-100'
                  : 'max-h-0 opacity-0'
              }`}
            >
              <p className="pt-2 pb-1.5 px-4 text-center text-[12.5px] leading-snug text-[#3C3C43]/70">
                Seleccioná al menos una vista para continuar.
              </p>
            </div>
            <div className="relative z-10">
              <button
                type="button"
                onClick={() => {
                  if (selectedVistas.length === 0) {
                    scrollToAndShake('vistas-grid-zone');
                    return;
                  }
                  onNext();
                }}
                aria-disabled={selectedVistas.length === 0}
                className={`w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover text-white text-[15.5px] font-semibold transition-colors ${
                  selectedVistas.length === 0 ? 'opacity-60' : ''
                }`}
              >
                {/* "Revisar catálogo" y no "Siguiente", igual que el CTA de desktop: el
                    paso de vistas NO es el último —lleva al review, donde se descartan
                    combinaciones antes de pagar (ADR-2026-08-27-catalogo-vuelve-el-review)—
                    y el botón tiene que decir a dónde lleva. En muebles el paso siguiente
                    es otro, así que ahí sigue siendo "Siguiente". */}
                {esMuebles ? 'Siguiente' : 'Revisar catálogo'}
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Desktop floating strip de seleccionadas — mismo estilo que el
          MultiSelectedStrip de multi-template (rounded-2xl + shadow,
          centrado, abajo). Solo se muestra cuando hay seleccionadas.
          Usamos `flex justify-center` con `left/right: 0` en vez de
          `translateX(-50%)` para que la animación `animate-mode-swap`
          (que setea su propio `transform`) no pise el centrado. */}
      {!isMobile && selectedVistas.length > 0 && (
        <div
          className="absolute left-0 right-0 z-30 px-8 pointer-events-none animate-mode-swap flex justify-center"
          style={{ bottom: '32px' }}
        >
          <div className="pointer-events-auto bg-white rounded-3xl shadow-xl ring-1 ring-black/[0.05] px-6 py-5 flex items-center gap-4 max-w-[720px] w-full">
            <div className="flex items-center gap-3 overflow-x-auto flex-1 scrollbar-hide pt-1.5 -my-1.5">
              {selectedVistas.map((v) => {
                // Mismo resolver que la grilla — ver la nota del strip mobile.
                const thumbUrl = resolveImageForSelected(v);
                return (
                <div key={`${v.kind}-${v.id}`} className="shrink-0 relative">
                  <div className="w-20 h-20 rounded-xl overflow-hidden bg-neutral-100 ring-1 ring-black/[0.05]">
                    {thumbUrl ? (
                      <img
                        src={thumbUrl}
                        alt={v.nombre}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[#3C3C43]/40 text-[11px] text-center px-1.5">
                        {v.nombre}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeFromSelected(v)}
                    aria-label={`Quitar ${v.nombre}`}
                    className="absolute -top-1.5 -right-1.5 w-6 h-6 inline-flex items-center justify-center rounded-full bg-black/65 text-white hover:bg-black/80 transition-colors"
                  >
                    <X className="w-3.5 h-3.5" strokeWidth={2.5} />
                  </button>
                </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
